import { InputTypeDetail } from '../../types/rawScenario';
import parsePhoneNumber from 'libphonenumber-js';

type TranslationResult = [string, null] | [null, string];

const toHankaku = (char: string) => {
  const codePoint = char.codePointAt(0);
  return codePoint != null ? String.fromCodePoint(codePoint - 0xFEE0) : '';
}
export const translateRules = {
  text: (value: string): TranslationResult => {
    return value !== '' ? [value, null] : [null, 'テキストを入力してください'];
  },
  'lower-alphabet': (value: string): TranslationResult => {
    const ret = value
      .replace(/[Ａ-Ｚａ-ｚ]/g, (v) => toHankaku(v))
      .replace(/[A-Z]/g, w => w.toLowerCase())
      .replace(/[^\na-z]/g, '')
    return ret !== '' ? [ret, null] : [null, 'アルファベット小文字で入力してください'];
  },
  number: (value: string): TranslationResult => {
    const normalized = value
      .replace(/[０-９]/g, v => toHankaku(v))
      .replace(/[^0-9]/g, '');
    const num = normalized !== '' ? Number(normalized) : null;
    return num != null && !Number.isNaN(num) ? [normalized, null] : [null, '数字で入力してください'];
  },
  'number-period': (value: string): TranslationResult => {
    const ret = value
      .replace(/[．０-９]/g, v => toHankaku(v))
      .replace(/[^.0-9]/g, '');
    return ret !== '' ? [ret, null] : [null, '数字もしくはピリオドで入力してください'];
  },
  'number-slash': (value: string): TranslationResult => {
    const ret = value
      .replace(/[・]/g, '/')
      .replace(/[／０-９]/g, v => toHankaku(v))
      .replace(/[^/0-9]/g, '');
    return ret !== '' ? [ret, null] : [null, '数字もしくはスラッシュで入力してください'];
  },
  'number-hyphen': (value: string): TranslationResult => {
    const ret = value
      .replace(/[-－﹣−‐⁃‑‒–—﹘―⎯⏤ーｰ─━]/g, '-')
      .replace(/[０-９]/g, v => toHankaku(v))
      .replace(/[^-0-9]/g, '');
    return ret !== '' ? [ret, null] : [null, '数字もしくはハイフンで入力してください'];
  },
  'postal-code': (value: string): TranslationResult => {
    const ret = value
      .replace(/[０-９]/g, v => toHankaku(v))
      .replace(/[^0-9]/g, '')
      .replace(/^([0-9]{3})([0-9]{1,4})/, (w, $1, $2) => `${$1}-${$2}`);
    return ret !== '' ? [ret, null] : [null, '郵便番号を入力してください'];
  },
  'email': (value: string): TranslationResult => {
    if (value.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/)) {
      return [value, null];
    }
    return [null, "メールアドレスを入力してください"];
  },
  'kana': (value: string): TranslationResult => {
    if (value.trim().length === 0) {
      return [null, 'かなを入力してください'];
    }
    const ret = value
      .replace(/\s/g, '　')
      .replace(/[-－﹣−‐⁃‑‒–—﹘―⎯⏤ーｰ─━]/g, 'ー')
      .replace(/[ァ-ン]/g, w => String.fromCodePoint(w.codePointAt(0)! - 0x60))
      .replace(/[^あいうえおかきくけこさしすせそたちつてとなにぬねのはひふへほまみむめもやゆよらりるれろわをんがぎぐげござじずぜぞだぢづでどばびぶべぼぱぴぷぺぽぁぃぅぇぉゃゅょっー　]/g, '');
    return ret !== '' ? [ret, null] : [null, 'かな以外が含まれています'];
  },
  'phone-number': (value: string): TranslationResult => {
    const normalized = translateRules['number-hyphen'](value) || '';
    if (normalized[0] == null) {
      return [null, '電話番号を入力してください']
    }
    const phoneNumber = parsePhoneNumber(normalized[0], 'JP');
    if (phoneNumber && phoneNumber.isValid()) {
      const nationalNumber = phoneNumber.formatNational();
      // 頭の0が落ちているときはフォーマットエラーとする
      if (nationalNumber[0] === normalized[0][0]) {
        return [phoneNumber.formatNational(), null];
      }
    }
    return [null, '電話番号を入力してください'];
  }
} as const;

export type TranslateRule = keyof typeof translateRules;

export const translateValue = (rule: TranslateRule | undefined, value: string): TranslationResult => {
  return translateRules[rule || 'text'](value);
}


export function getTextTranslateRule(typeDetail: InputTypeDetail): TranslateRule {
  switch (typeDetail) {
    case '10':
      return 'lower-alphabet';
    case '50':
      return 'text';
    case '20':
      return 'number';
    case '21':
      return 'number-period';
    case '30':
      return 'number-slash';
    case '40':
      return 'number-hyphen';
  }
}
